import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    DoCheck,
    ElementRef,
} from '@angular/core';
import {
    FormGroup,
    FormControl,
    FormArray,
    Validators,
    ValidatorFn,
    AbstractControl,
    ValidationErrors
} from '@angular/forms';

import { Subscription, combineLatest, debounceTime } from 'rxjs';
import { finalize, filter, tap } from 'rxjs/operators';
import { cloneDeep, isEqual } from 'lodash';
import { MessageService } from 'primeng/api';
import moment from 'moment-timezone';

import { MediaSubscriptionApiService } from '../../../../api/mediaSubscriptions/media-subscription.api.service';
import { mediaTypeToNetwork } from '@Social/Core/enums/network-type.enum';
import { RelativeTimePipe } from '../../../../Common/Misc/pipes/RelativeTime/relative-time.pipe';
import { MediaSubscriptionService } from '../../../../Monitor/Core/services/media-subscription.service';
import { MediaSubscription, MediaTypeRequest } from '../../../../Monitor/Shared/models/media-subscription.model';
import { NetworkTypeEnum, networkOptionsOrder as networkOrder  } from '../../../Core/enums/network-type.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MediaSubscriptionStatusEnum as StatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { networkTypeToMediaSubTypeMap, mediaSubTypeToNetworkTypeMap } from '../../models/network-type-to-media-sub-type.map';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ThemeService } from '../../services/theme.service';
import { AgentService } from '../../services/agent.service';
import { QuotaInformation } from '../../../../api/mediaSubscriptions/models/quota-information.api.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { getMonitoringDateQueryInputTopicOptions } from '../../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { SocialArchiveSearchService } from '../../services/social-archive-search.service';
import { AgentWidgetComponent, Handle } from '../AgentWidget/agent-widget.component';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { MediaRequestType } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MonitoringQuotaTypesEnum } from '@api/mentions/enum/monitoring-quota-types.enum';

export enum ArchiveSearchMode {
    create,
    edit,
    saveAs, // save as social topic
}

enum GeneralFormTypesEnum {
    NETWORKS = 'NETWORKS',
    KEYWORDS = 'KEYWORDS',
    HANDLES = 'HANDLES'
}

@Component({
    selector: 'agpr-archive-search-widget',
    templateUrl: './archive-search-widget.component.html',
    styleUrls: ['./archive-search-widget.component.scss'],
    standalone: false
})
export class ArchiveSearchWidgetComponent extends AgentWidgetComponent implements OnInit, DoCheck, OnDestroy {

    @Input() archiveMode: ArchiveSearchMode = ArchiveSearchMode.create;
    @Input() set searchModel(archiveSearch: MediaSubscription) {
        this.archiveSearch = cloneDeep(archiveSearch);
    }

    override readonly id: string = 'archiveSearchWidget';
    readonly archiveSearchMode = ArchiveSearchMode;
    readonly dateOptions = getMonitoringDateQueryInputTopicOptions();

    searchRange: SearchRangeModel = null;
    cachedArchiveSearch: MediaSubscription;

    showResetMessage: boolean = false;
    showEstimate: boolean = false;
    errorTooltip: string;

    quotaInfo: QuotaInformation;
    totalUsageCount: number = 0;
    estimatedTotalUsageCount: number = 0;

    oldSearchNameNetworks: string = '';
    oldSearchNameKeywords: string = '';
    oldSearchNameHandles: string = '';

    archiveForm: FormGroup;

    private archiveSearch: MediaSubscription;
    private initialKeywords: IncludeExcludeModel <string>;
    private initialNetworks: NetworkTypeEnum[];
    private initialHandles: Handle[];
    protected subscriptions: Subscription[] = [];

    constructor(
        coreDialogService: CoreDialogService,
        mediaSubscriptionService: MediaSubscriptionService,
        agentService: AgentService,
        localizationService: LocalizationService,
        themeService: ThemeService,
        messageService: MessageService,
        monitorConfigService: MonitorConfigService,
        mediaSubscriptionApiService: MediaSubscriptionApiService,
        public currentUserStateService: CurrentUserStateService,
        elementRef: ElementRef,
        private socialArchiveSearchService: SocialArchiveSearchService,
    ) {
        super(
            coreDialogService,
            agentService,
            mediaSubscriptionService,
            localizationService,
            themeService,
            messageService,
            monitorConfigService,
            mediaSubscriptionApiService,
            currentUserStateService,
            elementRef
        );

        this.themeOptionFetcher = this.themeService.all$;
        this.relativeTimePipe = new RelativeTimePipe(this.localizationService);

        this.archiveForm = new FormGroup({
            header: new FormGroup({
                newSearch: new FormControl<boolean>(true), // @TODO use a single formcontrol for radio selection
                existingSearch: new FormControl<boolean>(false),
                existingAgent: new FormControl<MediaSubscription>(null)
            }, {
                validators: this.noTopicValidator()
            }),
            date: new FormControl<DateQueryModel>({ value: DateQueryModel.getLast24HoursQuery(), disabled: false }),
            name: new FormControl({ value: '', disabled: true }, [
                Validators.required,
                this.uniqueNameValidator()
            ]),
            general: new FormGroup({
                section: new FormGroup({
                    networks: new FormControl<NetworkTypeEnum[]>([],
                        Validators.required
                    ),
                    keywords: new FormControl(new IncludeExcludeModel<string>(), [
                        this.maxKeywordValidator(),
                        this.hashtagValidator(),
                        this.keywordCharValidator()
                    ]),
                }),
                handles: new FormArray([]),
            }, {
                validators: this.noHandleOrKeywordValidator()
            }),
            status: new FormControl<StatusEnum>({ value: StatusEnum.PAUSED, disabled: false }),
            themes: new FormControl([])
        });
    }

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.push(
            combineLatest([
                this.agentService.all$.pipe(filter(x => !!x)),
                this.monitorConfigService.getAvailableSocialMediaTypes().pipe(filter(x => !!x))
            ]).subscribe(([agents, mediaTypes]) => {
                this.agents = agents;
                this.allowedNetworks = mediaTypes
                    .map(media => mediaTypeToNetwork.get(media))
                    .filter(x => !!x);

                if (this.allowedNetworks.includes(NetworkTypeEnum.reddit)) {
                    this.allowedNetworks.push(NetworkTypeEnum.subReddit);
                }

                this.handles.clear();

                switch (this.archiveMode){
                    case ArchiveSearchMode.create:
                        this.headerTitle = 'Social.archiveSearch.title';
                        // add default handles based on provisioned networks
                        networkOrder.forEach(network => {
                            if (this.allowedNetworks.includes(network)) {
                                this.handles.push(
                                    new FormGroup({
                                        handleValues: new FormControl([], ),
                                        type: new FormControl(network)
                                    },
                                    {
                                        validators: this.uniqueHandleValidator()
                                    })
                                );
                            }
                        });

                        this.archiveForm.patchValue({
                            name: this.findDefaultAgentName(this.agents),
                            general: {
                                section: {
                                    networks: this.allowedNetworks,
                                    keywords: new IncludeExcludeModel(),
                                },
                            }
                        });

                        this.cachedArchiveSearch = cloneDeep(this.archiveSearch);

                        break;

                    case ArchiveSearchMode.edit:
                    case ArchiveSearchMode.saveAs:
                        if (!this.archiveSearch) {
                            console.error('Must provide a searchModel when archive-search-widget is in edit or saveAs mode');
                            return;
                        }

                        const networkTypes: NetworkTypeEnum[] = this.archiveSearch.mediaTypeRequests
                            .map(x => mediaSubTypeToNetworkTypeMap.get(x.mediaType))
                            .filter(network => this.allowedNetworks.includes(network));

                        if (networkTypes.includes(NetworkTypeEnum.subReddit) && !networkTypes.includes(NetworkTypeEnum.reddit)) {
                            networkTypes.push(NetworkTypeEnum.reddit);
                        }

                        if (networkTypes.includes(NetworkTypeEnum.reddit) && !networkTypes.includes(NetworkTypeEnum.subReddit)) {
                            networkTypes.push(NetworkTypeEnum.subReddit);
                        }

                        const handles: Handle[] = this.archiveSearch.mediaTypeRequests
                            .filter(request => {
                                const networks = mediaSubTypeToNetworkTypeMap.get(request.mediaType);
                                return this.allowedNetworks.includes(networks);
                            })
                            .map(media => {
                                return <Handle>{
                                    handleValues: media.handles,
                                    type: mediaSubTypeToNetworkTypeMap.get(media.mediaType)
                                };
                            }).sort((a, b) => {
                                return networkOrder.indexOf(a.type) - networkOrder.indexOf(b.type);
                            });

                        // add handles based on network selection
                        networkTypes.forEach(network => {
                            this.handles.push(
                                new FormGroup({
                                    handleValues: new FormControl([]),
                                    type: new FormControl(network)
                                },
                                {
                                    validators: this.uniqueHandleValidator()
                                })
                            );
                        });


                        this.handles.controls.sort((a, b) => {
                            return networkOrder.indexOf(a.get('type').value) - networkOrder.indexOf(b.get('type').value);
                        });

                        if (this.archiveMode === ArchiveSearchMode.edit) {
                            this.headerTitle = 'Social.archiveSearch.title.edit';

                            this.archiveForm.patchValue({
                                general: {
                                    section: {
                                        networks: networkTypes,
                                        keywords: new IncludeExcludeModel({
                                            includeAll: this.archiveSearch.keywords.and,
                                            include: this.archiveSearch.keywords.or,
                                            exclude: this.archiveSearch.keywords.not
                                        })
                                    },
                                    handles: handles
                                },
                            });
                            this.cachedArchiveSearch = cloneDeep(this.archiveSearch);
                        } else if (this.archiveMode === ArchiveSearchMode.saveAs) {
                            this.headerTitle = 'Social.archiveSearch.title.saveAsSocialTopic';

                            this.archiveForm.patchValue({
                                name: this.findDefaultAgentName(this.agents),
                                general: {
                                    section: {
                                        networks: networkTypes,
                                        keywords: new IncludeExcludeModel({
                                            includeAll: this.archiveSearch.keywords.and,
                                            include: this.archiveSearch.keywords.or,
                                            exclude: this.archiveSearch.keywords.not
                                        })
                                    },
                                    handles: handles
                                },
                                status: StatusEnum.PAUSED,
                                themes: this.archiveSearch.themeIds
                            });

                            this.name.enable();
                            this.archiveSearch.name = this.findDefaultAgentName(this.agents);
                            this.cachedArchiveSearch = cloneDeep(this.archiveSearch);
                        }

                        break;

                    default:
                        console.error('Archive-widget invalid mode');
                        break;
                }
            }),
            this.mediaSubscriptionService.getQuotaInfo(MonitoringQuotaTypesEnum.SOCIAL_LISTENING).subscribe(quota => {
                if (quota) {
                    this.quotaInfo = quota;
                    this.totalUsageCount = this.quotaInfo.currentCount;
                    this.estimatedTotalUsageCount = this.totalUsageCount / moment().date() * (moment().daysInMonth() - moment().date());
                }
            }),
            this.monitorConfigService.getSearchRange().subscribe(searchRange => this.searchRange = searchRange),
            this.socialArchiveSearchService.searchDateRange$.subscribe(searchDateRange => {
                if (searchDateRange && this.archiveMode === ArchiveSearchMode.edit) {
                    this.archiveForm.patchValue({
                        date: DateQueryModel.clone(searchDateRange)
                    });
                }
            }),
            this.archiveForm.valueChanges.pipe(debounceTime(300)).subscribe(formValues => {
                this.updateSearch(formValues);
            }),
            this.keywords.valueChanges.subscribe(() => {
                this.getEstimate();
                this.checkSearchPropertyChange(GeneralFormTypesEnum.KEYWORDS,this.oldSearchNameKeywords);
            }),
            this.handles.valueChanges.subscribe(() => {
                this.getEstimate();
                this.checkSearchPropertyChange(GeneralFormTypesEnum.HANDLES, this.oldSearchNameHandles);
            }),
            this.networks.valueChanges.subscribe(networks => {
                this.getEstimate();
                this.checkSearchPropertyChange(GeneralFormTypesEnum.NETWORKS, this.oldSearchNameNetworks);

                if (!networks.includes(NetworkTypeEnum.reddit)) {
                    // remove sub reddit if reddit is not selected
                    const index = networks.indexOf(NetworkTypeEnum.subReddit);
                    if (index !== -1) {
                        networks.splice(index, 1);
                    }
                } else {
                    // add sub reddit if reddit is selected
                    if (!networks.some(network => network === NetworkTypeEnum.subReddit)) {
                        networks.push(NetworkTypeEnum.subReddit);
                    }
                }

                // after remove an item, new this.handles index is 1 less than old index
                let numOfRemovedItems = 0;
                this.handles.value.forEach(
                    (handle: Handle, index: number) => {
                        if (!networks.includes(handle.type)) {
                            this.handles.removeAt(index - numOfRemovedItems);
                            numOfRemovedItems ++;
                        }
                    }
                );

                // add missing handles that have network selections
                networks.forEach(network => {
                    if (!this.handles.value.find(handle => handle.type === network)) {
                        this.handles.push(
                            new FormGroup({
                                handleValues: new FormControl([]),
                                type: new FormControl(network)
                            },
                            {
                                validators: this.uniqueHandleValidator()
                            })
                        );
                    }
                });

                this.handles.controls.sort((a, b) => {
                    return networkOrder.indexOf(a.get('type').value) - networkOrder.indexOf(b.get('type').value);
                });
            }),
            this.archiveForm.get('themes').valueChanges.subscribe(themes => {
                if (themes.length === 0) {
                    this.status.patchValue(StatusEnum.PAUSED);
                    this.status.disable();
                } else {
                    this.status.enable();
                }
            }),
            this.archiveForm.get('header.newSearch').valueChanges.subscribe((state: boolean) => {
                if (state && !this.hasChangesFromDefault()) {
                    this.newSearch.patchValue(true, { onlySelf: true, emitEvent: false });
                    this.existingSearch.patchValue(false, { onlySelf: true, emitEvent: false });
                    if (this.archiveMode === ArchiveSearchMode.saveAs) {
                        this.name.enable();
                    }
                    return;
                }
                const confirmDialogModel: ConfirmDialogModel = {
                    inputs: {
                        heading: 'Social.archiveSearch.confirmationDialog.header',
                        showCancel: true,
                        alertType: messageSeverityEnum.warning,
                        alertMessage: 'Social.archiveSearch.confirmationDialog.message',
                        okLabel: 'continue'
                    },
                    outputs: {
                        dialogClose:  (confirmed: boolean) => {
                            if (confirmed) {
                                this.existingSearch.patchValue(!state, { onlySelf: true, emitEvent: false });
                                if (state) {
                                    this.archiveSearch.id = null;
                                    this.archiveSearch.name = null;

                                    this.setArchiveSearch(this.cachedArchiveSearch);
                                    this.initialKeywords = this.keywords.value.clone();
                                    this.initialNetworks = [...this.networks.value];
                                    this.initialHandles = cloneDeep(this.handles.value);

                                    if (this.archiveMode === ArchiveSearchMode.saveAs) {
                                        this.name.enable();
                                    } else {
                                        this.name.disable();
                                    }
                                }
                            } else {
                                this.newSearch.patchValue(!state, { onlySelf: true, emitEvent: false });
                            }
                            this.header.updateValueAndValidity();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);

            }),
            this.archiveForm.get('header.existingSearch').valueChanges.subscribe(state => {
                if (state && !this.hasChangesFromDefault()) {
                    this.newSearch.patchValue(false, { onlySelf: true, emitEvent: false });
                    this.existingSearch.patchValue(true, { onlySelf: true, emitEvent: false });
                    return;
                }
                const confirmDialogModel: ConfirmDialogModel = {
                    inputs: {
                        heading: 'Social.archiveSearch.confirmationDialog.header',
                        showCancel: true,
                        alertType: messageSeverityEnum.warning,
                        alertMessage: 'Social.archiveSearch.confirmationDialog.message',
                        okLabel: 'continue'
                    },
                    outputs: {
                        dialogClose:  (confirmed: boolean) => {
                            if (confirmed) {
                                this.newSearch.patchValue(!state, { onlySelf: true, emitEvent: false });
                                this.setArchiveSearch(this.cachedArchiveSearch);
                                this.initialKeywords = this.keywords.value.clone();
                                this.initialNetworks = [...this.networks.value];
                                this.initialHandles = cloneDeep(this.handles.value);
                                if (state) {
                                    this.name.disable();
                                }
                            } else {
                                this.existingSearch.patchValue(!state, { onlySelf: true, emitEvent: false });
                            }
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);

            }),
            this.archiveForm.get('header.existingAgent').valueChanges.subscribe(agent => {
                if (!agent) {
                    return;
                }

                this.name.enable();
                this.name.clearValidators();
                this.name.updateValueAndValidity();
                this.archiveSearch = agent.clone();
                this.archiveSearch.id = agent.id;
                this.archiveSearch.name = agent.name;
                this.setArchiveSearch(this.archiveSearch);
            }),
        );
        this.initialKeywords = this.keywords.value.clone();
        this.initialNetworks = [...this.networks.value];
        this.initialHandles = cloneDeep(this.handles.value);
    }

    override ngDoCheck() {
        this.errorTooltip = !this.archiveForm.valid ? this.localizationService.transform('Social.agentWidget.error.general.heading') + '\n' : '';

        if (this.section.errors?.['invalidCharKeyword']) {
            this.errorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keyword.invalidCharacter')}\n`;
        }
        if (this.keywords.errors?.['hashtagSymbol']) {
            this.errorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keyword.hashtag')}\n`;
        }
        if (this.keywords.errors?.['maxKeyword']) {
            this.errorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keywordMaximum')}\n`;
        }
        if (this.name.errors?.['duplicateName']) {
            this.errorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.nameExists')}\n`;
        }
        if (this.name.errors?.['required']) {
            this.errorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.nameMinLength')}\n`;
        }
        if (this.networks.errors?.['required']) {
            this.errorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.noNetwork')}\n`;
        }
        if (this.handles.controls.some(x => x.errors?.['duplicateHandle'])) {
            this.saveErrorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.handle.duplicate')}\n`;
        }
        if (this.general.errors?.['noHandleAndKeyword']) {
            this.errorTooltip += `• ${this.localizationService.transform('Social.agentWidget.error.keywordOrHandle')}\n`;
        }
    }


    get header(): FormControl {
        return this.archiveForm.get('header') as FormControl;
    }

    get newSearch(): FormControl {
        return this.archiveForm.get('header.newSearch') as FormControl;
    }

    get existingSearch(): FormControl {
        return this.archiveForm.get('header.existingSearch') as FormControl;
    }

    get existingAgent(): FormControl {
        return this.archiveForm.get('header.existingAgent') as FormControl;
    }

    get date(): FormControl {
        return this.archiveForm.get('date') as FormControl;
    }

    get name(): FormControl {
        return this.archiveForm.get('name') as FormControl;
    }

    get general(): FormGroup {
        return this.archiveForm.get('general') as FormGroup;
    }

    get section(): FormGroup {
        return this.archiveForm.get('general.section') as FormGroup;
    }

    get networks(): FormControl {
        return this.archiveForm.get('general.section.networks') as FormControl;
    }

    get keywords(): FormControl {
        return this.archiveForm.get('general.section.keywords') as FormControl;
    }

    get handles(): FormArray {
        return this.archiveForm.get('general.handles') as FormArray;
    }

    get themes(): FormControl {
        return this.archiveForm.get('themes') as FormControl;
    }

    get status(): FormControl {
        return this.archiveForm.get('status') as FormControl;
    }


    /**
     * Updates the search model with the current form values & updates the dirty flag
     * Note: shortcircuit logic is needed when form control is disabled and results in undefined
     */
    private updateSearch(formValues) {
        const mediaTypeRequests: MediaTypeRequest[] = formValues.general.section.networks.map((network: NetworkTypeEnum) => {
            const mediaSubType: MediaRequestType = networkTypeToMediaSubTypeMap.get(network);
            return {
                mediaType: mediaSubType,
                handles: formValues.general.handles.find((handle: Handle) => handle.type === network)?.handleValues
            };
        });

        const search = new MediaSubscription({
            id: this.archiveSearch?.id,
            label: formValues.name,
            state: formValues.status || StatusEnum.PAUSED,
            shouldTerms: formValues.general.section.keywords.include,
            mustTerms: formValues.general.section.keywords.includeAll,
            mustNotTerms: formValues.general.section.keywords.exclude,
            isAgent: true,
            mediaTypeRequests: mediaTypeRequests,
            themeIds: formValues.themes
        });

        this.archiveSearch = search;
    }

    /**
     * Determines if any changes have been made to the form compared to the initial values.
     */
    private hasChangesFromDefault(): boolean {
        let agentChanged = false;
        const keywordsChanged = !this.keywords.value.equals(this.initialKeywords, false);
        const networksChanged = !isEqual(this.networks.value.sort(), this.initialNetworks.sort());
        const handlesChanged = !isEqual(
            [...this.handles.value].sort((a, b) => a.type.localeCompare(b.type)),
            [...this.initialHandles].sort((a, b) => a.type.localeCompare(b.type))
        );
        if (this.existingAgent.value && this.cachedArchiveSearch) {
            agentChanged = !this.existingAgent.value.equals(this.cachedArchiveSearch);
        }
        const changed = keywordsChanged || networksChanged || handlesChanged || agentChanged;
        return changed;
    }


    /**
     * Create or update the archive search based on archiveMode
     */
    override save() {
        this.loading = true;
        if (this.archiveMode === ArchiveSearchMode.saveAs && this.existingSearch.value) {
            this.agentService.update(this.archiveSearch).pipe(
                finalize(() => {
                    this.loading = false;
                    this.closeDialog(true);
                })
            ).subscribe();
        } else {
            this.agentService.create(this.archiveSearch).pipe(
                tap((id) => this.archiveSearch.id = id),
                finalize(() => {
                    this.loading = false;
                    this.closeDialog(true);
                })
            ).subscribe();
        }
    }

    /** Perform archive search */
    search(): void {
        this.socialArchiveSearchService.archiveSearch(this.archiveSearch, this.date.value);
        this.closeDialog(true);
    }


    /** Update archiveForm with givin archive search based on archive mode */
    private setArchiveSearch(search?: MediaSubscription) {
        if (!search) {
            // set form to default values
            this.handles.clear({ emitEvent: false });
            this.allowedNetworks?.forEach(network => {
                this.handles.push(
                    new FormGroup({
                        handleValues: new FormControl([]),
                        type: new FormControl(network)
                    },{
                        validators: this.uniqueHandleValidator()
                    }),
                );
            });

            this.archiveForm.patchValue({
                name: this.findDefaultAgentName(this.agents),
                general: {
                    section: {
                        networks: this.allowedNetworks,
                        keywords: new IncludeExcludeModel(),
                    },
                },
                status: StatusEnum.PAUSED,
            });

            return;
        }

        const archiveSearch = cloneDeep(search);

        const archiveNetworks: NetworkTypeEnum[] = archiveSearch.mediaTypeRequests
            .map(x => mediaSubTypeToNetworkTypeMap.get(x.mediaType))
            .filter(network => this.allowedNetworks.includes(network));

        const archiveHandles: Handle[] = archiveSearch.mediaTypeRequests
            .filter(request => {
                const networks = mediaSubTypeToNetworkTypeMap.get(request.mediaType);
                return this.allowedNetworks.includes(networks);
            })
            .map(media => {
                return <Handle>{
                    handleValues: media.handles,
                    type: mediaSubTypeToNetworkTypeMap.get(media.mediaType)
                };
            }).sort((a, b) => {
                return networkOrder.indexOf(a.type) - networkOrder.indexOf(b.type);
            });

        this.handles.clear({ emitEvent: false });

        archiveNetworks?.forEach(network => {
            this.handles.push(
                new FormGroup({
                    handleValues: new FormControl([]),
                    type: new FormControl(network)
                },{
                    validators: this.uniqueHandleValidator()
                })
            );
        });

        if (this.archiveMode === ArchiveSearchMode.create || this.archiveMode === ArchiveSearchMode.edit) {
            this.archiveForm.patchValue({
                general: {
                    section: {
                        networks: archiveNetworks,
                        keywords: new IncludeExcludeModel({
                            includeAll: archiveSearch.keywords.and,
                            include: archiveSearch.keywords.or,
                            exclude: archiveSearch.keywords.not
                        }),
                    },
                    handles: archiveHandles
                }
            });
        } else if (this.archiveMode === ArchiveSearchMode.saveAs) {
            this.archiveForm.patchValue({
                name: archiveSearch.name,
                general: {
                    section: {
                        networks: archiveNetworks,
                        keywords: new IncludeExcludeModel({
                            includeAll: archiveSearch.keywords.and,
                            include: archiveSearch.keywords.or,
                            exclude: archiveSearch.keywords.not
                        })
                    },
                    handles: archiveHandles
                },
                status: archiveSearch.status,
                themes: archiveSearch.themeIds
            });
        }
    }

    override agentChanged() {
        if (this.archiveMode === ArchiveSearchMode.saveAs) {
            this.isDirty = !this.archiveSearch.equals(this.cachedArchiveSearch);
        }
    }

    resetSearchProperties() {
        this.setArchiveSearch(this.existingAgent.value);
        this.showResetMessage = false;
    }

    private checkSearchPropertyChange(formControlType: GeneralFormTypesEnum, oldSearchName: string) {
        this.showResetMessage = false;

        if ((this.archiveMode === ArchiveSearchMode.create || this.archiveMode === ArchiveSearchMode.edit)
            && this.existingAgent.value
        ) {
            // check if the valueChanges are triggered by changing to a new search
            if (formControlType) {
                let searchSwitched: boolean = false, newName = null;
                if ((this.newSearch?.value && this.existingAgent.value.name)) {
                    searchSwitched = true;
                    newName = '';
                }
                if (this.existingSearch?.value && oldSearchName !== this.existingAgent.value?.name) {
                    searchSwitched = true;
                    newName = this.existingAgent.value?.name;
                }
                if (searchSwitched) {
                    switch (formControlType) {
                        case GeneralFormTypesEnum.NETWORKS:
                            this.oldSearchNameNetworks = newName;
                            break;
                        case GeneralFormTypesEnum.KEYWORDS:
                            this.oldSearchNameKeywords = newName;
                            break;
                        case GeneralFormTypesEnum.HANDLES:
                            this.oldSearchNameHandles = newName;
                            break;
                    }
                    return;
                }
            }

            if (!this.archiveSearch.keywords.equals(this.existingAgent.value.keywords, false)) {
                this.showResetMessage = true;
            }

            const newMediaTypeRequests = this.existingAgent.value.mediaTypeRequests
                .map(type => {
                    return {
                        mediaType: type.mediaType,
                        handles: type.handles
                    } as MediaTypeRequest;
                })
                .slice()
                .sort((a, b) => a.mediaType.localeCompare(b.mediaType));

            const oldMediaTypeRequests = this.archiveSearch.mediaTypeRequests
                .map(type => {
                    return {
                        mediaType: type.mediaType,
                        handles: type.handles
                    } as MediaTypeRequest;
                })
                .slice()
                .sort((a, b) => a.mediaType.localeCompare(b.mediaType));

            if (!isEqual(oldMediaTypeRequests, newMediaTypeRequests)) {
                this.showResetMessage = true;
            }
        }
    }

    /**
     * Compares the name value to existing agent names
     * If not unique, an error object with the key 'duplicateName' will be returned
     * @TODO consider an async validator with agentService.isNameDuplicated$() also see NEP-32699
     */
    uniqueNameValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const matchFound = this.agents.find(x => x.name?.toLocaleLowerCase() === control.value?.toLocaleLowerCase());

            return matchFound ? { duplicateName: true } : null;
        };
    }

    private noTopicValidator(): ValidatorFn {
        return (group: FormGroup): ValidationErrors | null => {

            const isExistingSearch: boolean = group.get('existingSearch').value;
            const existingAgent: MediaSubscription = group.get('existingAgent').value;

            const matchFound = (isExistingSearch && !existingAgent);

            return matchFound ? { noTopic: true } : null;
        };
    }

    override getEstimate() {
        const hasTwitter: boolean = this.networks.value.includes(NetworkTypeEnum.twitter);
        const twitterHandles: string[] = this.handles.value.find((handle: Handle) => handle.type === NetworkTypeEnum.twitter)?.handleValues;
        const noKeywords =
            this.keywords.value.includeAll.length === 0 &&
            this.keywords.value.include.length === 0 &&
            this.keywords.value.exclude.length === 0;

        this.getUsageEstimate(hasTwitter, twitterHandles, noKeywords);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
